import { Service } from 'typedi';
import { makeAutoObservable, toJS } from 'mobx';
import { uniqBy, first } from 'lodash';
import { NotificationContent, Notifications } from '../../Notifications';
import { PresetEntity, PresetService } from '../../../services/PresetService';
import i18n from '../../../i18n';
import { InvoiceItemService } from '../../../services/InvoiceItemService';
import {
    AnimanaInvoiceItemWithVariantDetail,
    BulkUpdateInvoiceItem,
    BulkUpdatesBody,
    CornerstoneInvoiceItemWithVariantDetail,
    DEFAULT_PRICING_DECIMALS,
    GetItemQuery,
    InvoiceItem,
    InvoiceItemTypes,
    InvoiceItemWithVariantDetail,
} from '../../../core/models';
import { ItemsGroupByType } from '../../../core/models/organization/settings/enum';
import { ConstantsStore } from '../../../store/constants.store';
import { QueueService } from '../../../services/queue/QueueService';
import { LookupsMap, LookupsService, Nullable } from '../../../services/lookups';
import {
    Lookup,
    LookupAnalysisGroup,
    LookupClassification,
    LookupDiscount,
    LookupDispensingItem,
    LookupItemTypes,
    LookupPricingChangePolicies,
    LookupRoundingPrecisions,
    LookupSpecialActionAvailabilities,
    LookupSupplier,
    LookupTax,
    LookupType,
    LookupUnitsOfMeasure,
} from '../../../core/models/datasource/lookups';
import { DatasourceFullSiteInfo } from '../../../core/models/datasource/sites';
import { RouterPaths } from '../../../router';
import { FEATURES, Services } from '@enterprise/core';
import { ToastWithSubHeader } from '../../../components/toasts/ToastWithSubheader';
import { InvoiceItemEditType } from './InvoiceItemEditType.enum';
import { PriceDeltaUpdateEntity } from './formType/BulkPriceEditFormType';
import { BulkStatusEditInfo } from './formType/BulkStatusEditFormType';
import { PatchUpdateModel } from './formType/bulkPatchEditFormType';
import { PatchDeltaUpdate } from '../../../core/models/datasource/invoiceItems/parts/patchDeltaUpdate';
import { PaginationModel } from '../../../store/invoiceItem.store';
import { EntityFormType } from '@enterprise/common';

enum Loadable {
    CommonInvoiceItemLookups = 'CommonInvoiceItemLookups',
    Presets = 'Presets',
}

export enum BulkUpdateFlyoverType {
    Price = 'price',
    Status = 'status',
    Description = 'description',
    Classification = 'classification',
    AddToPractices = 'addToPractices',
    SupplierName = 'supplierName',
    SupplierCode = 'supplierCode',
    AnalysisGroup1 = 'analysisGroup1',
    AnalysisGroup2 = 'analysisGroup2',
    AnalysisGroup3 = 'analysisGroup3',
}

interface FetchInvoiceItemParams {
    id?: string;
    description?: string;
    practice?: string;
    isSub?: boolean;
}

export interface BulkUpdateParams {
    items?: BulkUpdateInvoiceItem[];
    includedItems?: string[];
    excludedItems?: string[];
    update: PriceDeltaUpdateEntity | BulkStatusEditInfo | PatchUpdateModel;
    type: BulkUpdateFlyoverType;
    itemsSearchQuery?: GetItemQuery;
    pagination?: PaginationModel;
}

@Service()
export class ItemsPageStore {
    discounts: Lookup<LookupDiscount>[] = [];

    dispensingItems: Lookup<LookupDispensingItem>[] = [];

    filter?: GetItemQuery;

    isLoaded = false;

    isLoadingItem = false;

    isLoadingItems = false;

    private _invoiceItem?: InvoiceItemWithVariantDetail;

    private _invoiceItems: InvoiceItemWithVariantDetail[] = [];

    isCreatingNewItem = false;

    itemTypes: Lookup<LookupItemTypes>[] = [];

    presets: PresetEntity[] = [];

    pricingChangePolicies: Lookup<LookupPricingChangePolicies>[] = [];

    roundingPrecisions: Lookup<LookupRoundingPrecisions>[] = [];

    selectedItems = new Set<InvoiceItemWithVariantDetail>();

    specialActionAvailabilities: Lookup<LookupSpecialActionAvailabilities>[] = [];

    taxes: Lookup<LookupTax>[] = [];

    unitsOfMeasure: Lookup<LookupUnitsOfMeasure>[] = [];

    suppliers: Lookup<LookupSupplier>[] = [];

    analysisGroups: Lookup<LookupAnalysisGroup>[] = [];

    private queueService = new QueueService<Loadable>()
        .register(Loadable.Presets, async () => {
            this.presets = await this.presetService.getPresets();
        })
        .register(Loadable.CommonInvoiceItemLookups, async () => {
            const itemLookupGroup = [
                LookupType.DISCOUNTS,
                LookupType.DISPENSING_ITEMS,
                LookupType.ITEM_TYPES,
                LookupType.PRICING_CHANGE_POLICY,
                LookupType.ROUNDING,
                LookupType.SPECIAL_ACTION_TYPES,
                LookupType.TAXES,
                LookupType.UNITS,
                LookupType.SUPPLIERS,
                LookupType.ANALYSIS_GROUPS,
            ];
            const lookups = await this.lookupsService.getLookupsAsMap(itemLookupGroup);
            this.discounts = lookups.discounts;
            this.dispensingItems = lookups.dispensingItems;
            this.itemTypes = this.filterDisabledItemTypes(lookups.invoiceItemTypes);
            this.pricingChangePolicies = lookups.pricingChangePolicy;
            this.roundingPrecisions = lookups.roundingPrecisions;
            this.taxes = lookups.taxes;
            this.unitsOfMeasure = lookups.unitsOfMeasure;
            this.suppliers = lookups.suppliers;
            this.analysisGroups = lookups.analysisGroups;
        });

    constructor(
        private readonly constantsStore: ConstantsStore,
        private readonly invoiceItemService: InvoiceItemService,
        private readonly lookupsService: LookupsService,
        private readonly presetService: PresetService,
        private readonly featureManager: Services.FeatureManager,
    ) {
        makeAutoObservable(this, {
            filterDisabledItemTypes: false,
            getLookupResults: false,
            isSelected: false,
            isAnyItemSelected: false,
            areAllItemsSelected: false,
            getEditUrl: false,
            getSuccessMessage: false,
        });
    }

    get invoiceItem(): InvoiceItemWithVariantDetail | undefined {
        return toJS(this._invoiceItem);
    }

    get invoiceItems(): InvoiceItemWithVariantDetail[] {
        return toJS(this._invoiceItems);
    }

    get applications(): DatasourceFullSiteInfo[] {
        return toJS(this.constantsStore.applications);
    }

    get classifications(): Lookup<LookupClassification>[] {
        return toJS(this.constantsStore.classifications);
    }

    get isAnimanaOnly(): boolean {
        return this.constantsStore.applications.every(({ pimsName }) => pimsName?.toLowerCase() === 'animana');
    }

    get formType(): EntityFormType<AnimanaInvoiceItemWithVariantDetail | CornerstoneInvoiceItemWithVariantDetail> {
        return this.isAnimanaOnly
            ? new EntityFormType(AnimanaInvoiceItemWithVariantDetail)
            : new EntityFormType(CornerstoneInvoiceItemWithVariantDetail);
    }

    get uniqueItemsForBulkUpdate(): InvoiceItem[] {
        return Array.from(this.selectedItems)
            .map((itemGroup) => itemGroup.variants)
            .reduce((acc, val) => acc.concat(val), []);
    }

    get itemsGroupBy(): ItemsGroupByType {
        return this.constantsStore.organizationSettings?.itemsGroupBy || ItemsGroupByType.Id;
    }

    async load(toLoad: Loadable[] = Object.values(Loadable)) {
        this.isLoaded = false;
        // Presets are not supported in the new UI so we do not load them.
        if (this.featureManager.isAvailable(FEATURES.ENHANCED_PRODUCT_SEARCH) && this.featureManager.isAvailable(FEATURES.ENHANCED_SEARCH_UI)) {
            toLoad = toLoad.filter(function (e) {
                return e !== Loadable.Presets;
            });
        }
        await this.queueService.load(toLoad);
        this.isLoaded = true;
    }

    async fetchInvoiceItem_legacy(query: GetItemQuery): Promise<InvoiceItemWithVariantDetail> {
        this.isLoadingItem = true;
        this._invoiceItem = await this.invoiceItemService.getItemWithVariantDetails(query);
        this.isLoadingItem = false;
        return toJS(this._invoiceItem);
    }

    async fetchInvoiceItem(params: FetchInvoiceItemParams): Promise<InvoiceItemWithVariantDetail> {
        const { id, description, practice, isSub } = params;
        const query: GetItemQuery = {
            id,
            description,
            siteId: practice,
            matchHospitalDescription: true,
            depth: 1,
        };

        this.isLoadingItem = true;
        const invoiceItems = await this.invoiceItemService.getItemsWithVariantDetails(query);
        this.isLoadingItem = false;

        if (invoiceItems.length !== 1) {
            if (isSub === true) {
                this._invoiceItem = invoiceItems.find((item) => item.itemParent) || invoiceItems[0];
            } else {
                if (this.itemsGroupBy === ItemsGroupByType.Id && !this.isAnimanaOnly) {
                    this._invoiceItem = invoiceItems.find((item) => item.pimsGeneratedId === query.id && !item.itemParent) || invoiceItems[0];
                } else {
                    this._invoiceItem = invoiceItems.find((item) => !item.itemParent) || invoiceItems[0];
                }
            }
        } else {
            this._invoiceItem = invoiceItems[0];
        }

        return toJS(this._invoiceItem);
    }

    async fetchInvoiceItems(criteria?: GetItemQuery): Promise<void> {
        this.isLoadingItems = true;
        this._invoiceItems = [];
        this.clearSelectedItems();
        try {
            this._invoiceItems = await this.invoiceItemService.getItemsWithVariantDetails({ ...criteria, depth: 0 });
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        } finally {
            this.isLoadingItems = false;
        }
    }

    async fetchDuplicateInvoiceItem(criteria?: GetItemQuery): Promise<InvoiceItemWithVariantDetail> {
        try {
            const invoiceItems = await this.invoiceItemService.getItemsWithVariantDetails({ ...criteria, depth: 0 });
            return invoiceItems[0];
        } catch (e) {
            if (e?.innerException?.response?.data.errors[0]?.responseTypeDescription !== 'Not Connected') {
                Notifications.fromException(e);
                throw e;
            } else {
                return e?.innerException?.response?.data.data[0];
            }
        }
    }

    createInvoiceItem(): void {
        this.isCreatingNewItem = true;

        this._invoiceItem = Object.assign(new InvoiceItemWithVariantDetail(), {
            difference: {
                master: {
                    isActive: true,
                    prices: {
                        minimumPrice: 0,
                        dispensingFee: 0,
                        decimals: DEFAULT_PRICING_DECIMALS,
                    },
                },
            },
        });

        this._invoiceItem.sites = this.applications;
        // return this.invoiceItem;
    }

    async save(items: Partial<InvoiceItem>[], message: NotificationContent): Promise<void> {
        try {
            const runAt = new Date();
            const name = items[0]?.hospitalDescription as string;

            await this.invoiceItemService.scheduleSave(items, { isUpdatingExistingItem: !this.isCreatingNewItem, name, runAt });
            Notifications.success(message);
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
        this.isCreatingNewItem = false;
    }

    async scheduleSave(items: Partial<InvoiceItem>[], runAt: Date = new Date(), message: NotificationContent, name?: string): Promise<void> {
        try {
            const scheduleName = name || `Scheduled save - ${items[0]?.hospitalDescription}`;
            await this.invoiceItemService.scheduleSave(items, { isUpdatingExistingItem: !this.isCreatingNewItem, name: scheduleName, runAt });
            Notifications.success(message);
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
        this.isCreatingNewItem = false;
    }

    filterDisabledItemTypes(lookups: Lookup<LookupItemTypes>[]): Lookup<LookupItemTypes>[] {
        const disabledItemTypes = new Set([InvoiceItemTypes.Group, InvoiceItemTypes.Pick, InvoiceItemTypes.Dispensing]);
        return lookups.map(({ results, ...rest }) => ({
            results: results.filter((item) => !disabledItemTypes.has(item.id)),
            ...rest,
        }));
    }

    getLookupResults<T extends object>(lookups: Lookup<T>[], siteId?: string, query?: Nullable<Partial<T>>): T[] {
        const masterSiteId = this.constantsStore.organizationSettings?.masterSite?.siteId;
        return toJS(LookupsMap.getLookupResults(lookups, siteId || masterSiteId, query));
    }

    selectItem(item: InvoiceItemWithVariantDetail) {
        if (this.isSelected(item)) {
            this.selectedItems.delete(item);
        } else {
            this.selectedItems.add(item);
        }

        this.selectedItems = new Set(this.selectedItems);
    }

    isSelected(item: InvoiceItemWithVariantDetail): boolean {
        return this.selectedItems.has(item);
    }

    get selectedItemsValue(): InvoiceItemWithVariantDetail[] {
        return [...this.selectedItems.values()];
    }

    isAnyItemSelected(): boolean {
        return this.selectedItems.size > 0;
    }

    areAllItemsSelected(): boolean {
        return this.invoiceItems.length === this.selectedItems.size;
    }

    selectAllItems() {
        if (this.areAllItemsSelected()) {
            this.clearSelectedItems();
        } else {
            this.selectedItems = new Set(this.invoiceItems);
        }
    }

    async createBulkUpdate({ items, update, type }: BulkUpdateParams): Promise<void> {
        const { name, updateAt, ...data } = update;
        const isScheduledUpdate = Boolean(updateAt);
        const priceUpdateTypes = [
            BulkUpdateFlyoverType.Classification,
            BulkUpdateFlyoverType.Description,
            BulkUpdateFlyoverType.SupplierName,
            BulkUpdateFlyoverType.SupplierCode,
            BulkUpdateFlyoverType.AnalysisGroup1,
            BulkUpdateFlyoverType.AnalysisGroup2,
            BulkUpdateFlyoverType.AnalysisGroup3,
        ];

        const bulkUpdatesBody: BulkUpdatesBody = {
            items,
            name: name || 'Bulk Update',
            runAt: updateAt || new Date(),
            statusUpdate: (type === BulkUpdateFlyoverType.Status && (data as BulkStatusEditInfo)) || undefined,
            priceUpdate: (type === BulkUpdateFlyoverType.Price && (data as PriceDeltaUpdateEntity)) || undefined,
            patchUpdate: (priceUpdateTypes.includes(type) && (data as PatchDeltaUpdate)) || undefined,
        };
        try {
            await this.invoiceItemService.createBulkUpdate(bulkUpdatesBody);
            this.clearSelectedItems();
            const practicesCount = uniqBy(items, (item) => item?.provider.siteId).length || undefined;
            Notifications.success(this.getBulkUpdateSuccessMessage(items?.length, practicesCount, isScheduledUpdate));
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
    }

    async savePreset(preset: PresetEntity): Promise<any> {
        try {
            const newPreset = await this.presetService.save(preset);
            this.presets.push(newPreset);
            Notifications.success(i18n.t('itemsPageStore:presetSaved', 'Search was saved successfully'));
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
    }

    async deletePreset(preset: PresetEntity): Promise<any> {
        try {
            await this.presetService.delete(preset.id);
            this.presets = this.presets.filter((p) => p.id !== preset.id);
            Notifications.success(i18n.t('itemsPageStore:presetDeleted', 'Search was deleted successfully'));
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
    }

    getEditUrl(item: InvoiceItem, editType = InvoiceItemEditType.Form) {
        const isSub = Boolean(item.itemParent);

        return this.itemsGroupBy === ItemsGroupByType.Id
            ? `${RouterPaths.ControlCenterPages.Items}/${editType}?id=${encodeURIComponent(item.pimsGeneratedId)}`
            : `${RouterPaths.ControlCenterPages.Items}/${editType}?description=${encodeURIComponent(item.hospitalDescription)}&isSub=${isSub}`;
    }

    dispose() {
        this.discounts = [];
        this.dispensingItems = [];
        this._invoiceItems = [];
        this.itemTypes = [];
        this.pricingChangePolicies = [];
        this.presets = [];
        this.roundingPrecisions = [];
        this.specialActionAvailabilities = [];
        this.taxes = [];
        this.unitsOfMeasure = [];
        this.suppliers = [];
        this.analysisGroups = [];

        this.isLoaded = false;
        this._invoiceItem = undefined;
        this.isCreatingNewItem = false;
        this.filter = undefined;

        this.clearSelectedItems();
    }

    clearItemState() {
        this._invoiceItem = undefined;
        this.isCreatingNewItem = false;
    }

    clearSelectedItems() {
        this.selectedItems = new Set<InvoiceItemWithVariantDetail>();
    }

    getSuccessMessage(items: Partial<InvoiceItem>[]) {
        const item = first(items);
        const count = items.length;
        const itemIdentifier =
            this.constantsStore.organizationSettings?.itemsGroupBy === ItemsGroupByType.Description || !item?.pimsId
                ? item?.hospitalDescription
                : `${item?.pimsId} (${item?.hospitalDescription})`;

        const successMessage = i18n.t('controlCenter:toastMessages:itemScheduled', {
            itemIdentifier,
            count,
            defaultValue: `Scheduled: update of ${itemIdentifier} to ${count} practices.`,
        });

        const subHead =
            String(items.find((item) => item.childItems?.length) || '') &&
            i18n.t('controlCenter:toastMessages:hasSubproducts', {
                count,
                defaultValue: `This item has subproducts that will also be added to the practice.`,
            });

        return ToastWithSubHeader({ message: successMessage, subMessage: subHead });
    }

    getAddItemToPracticesMessage(params: { practiceCount: number; isScheduledUpdate: boolean }): NotificationContent {
        const { practiceCount, isScheduledUpdate } = params;
        if (isScheduledUpdate) {
            const message = i18n.t('controlCenter:toastMessages:scheduledAdditionalPractices', {
                count: practiceCount,
                defaultValue: `Add Item to ${practiceCount} Practice(s) Successfully Scheduled`,
            });
            const subMessage = i18n.t('controlCenter:toastMessages:scheduledUpdateSubHead', {
                defaultValue: `View the update's status in the activity log.`,
            });

            return ToastWithSubHeader({ message, subMessage });
        }

        const message = i18n.t('controlCenter:toastMessages:additionalPractices', {
            count: practiceCount,
            defaultValue: `Adding Item to ${practiceCount} Additional Practice(s)`,
        });
        const subMessage = i18n.t('controlCenter:toastMessages:updateSubHead', { defaultValue: `Refresh page to see changes as they complete.` });

        return ToastWithSubHeader({ message, subMessage });
    }

    getBulkUpdateSuccessMessage(itemsCount: number = 0, practiceCount: number = 0, isScheduledUpdate: boolean): NotificationContent {
        let successMessage, subHead;
        if (isScheduledUpdate) {
            const message1 = i18n.t('controlCenter:toastMessages:scheduledBulkUpdateMessage1', {
                count: itemsCount,
                defaultValue: `Update of ${itemsCount} Item(s) at `,
            });
            const message2 = i18n.t('controlCenter:toastMessages:scheduledBulkUpdateMessage2', {
                count: practiceCount,
                defaultValue: `${practiceCount} Practice Successfully Scheduled`,
            });

            successMessage = message1 + message2;
            subHead = i18n.t('controlCenter:toastMessages:scheduledUpdateSubHead', { defaultValue: `View the update's status in the activity log.` });
        } else {
            const message1 = i18n.t('controlCenter:toastMessages:bulkUpdateMessage1', {
                count: itemsCount,
                defaultValue: `Updating ${itemsCount} Item(s) at `,
            });
            const message2 = i18n.t('controlCenter:toastMessages:bulkUpdateMessage2', {
                count: practiceCount,
                defaultValue: `${practiceCount} Practice(s)`,
            });

            successMessage = message1 + message2;
            subHead = i18n.t('controlCenter:toastMessages:updateSubHead', { defaultValue: `Refresh page to see changes as they complete.` });
        }
        return ToastWithSubHeader({ message: successMessage, subMessage: subHead });
    }
}
